// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useState } from 'react';

import { useEvolveGet, useEvolvePatch } from '@evolvemep/api-ts';
import { Flex, Loader, Text } from '@mantine/core';
import parse from 'html-react-parser';

import { WrappedModal } from 'components/Mantine/Modals/WrappedModal';
import { isNotNil } from 'helpers/isNotNil';
import { TriggerOnVisible } from 'hooks/useOnScreen';
import { useMaybeUser } from 'hooks/user/useMaybeUser';
import { useSignOut } from 'modules/Authentication/auth/useSignOut';

export const EULA_MODAL_Z_INDEX = 300002;

type Props = {
  opened?: boolean;
  onClose?: () => void;
  onEulaAccepted?: () => void;
};

export const EULAModal = ({ onEulaAccepted, opened, onClose }: Props) => {
  const { user, refreshUser, loading: loadingUser } = useMaybeUser();
  const signOut = useSignOut();
  const [accepted, setAccepted] = useState(user?.acceptedLatestEula ?? false);
  const { apiCall: acceptEula, loading: saving } = useEvolvePatch(`admin/user/acceptEula`);
  const { data, apiCall, loading } = useEvolveGet('admin/eula/latest');
  const [eulaEndReached, setEulaEndReached] = useState(accepted);

  const modalOpened = opened || (isNotNil(user) && !user.acceptedLatestEula);

  useEffect(() => {
    if (modalOpened) {
      void apiCall();
      setAccepted((a) => user?.acceptedLatestEula ?? a);
    }
  }, [apiCall, modalOpened, user?.acceptedLatestEula]);

  return (
    <WrappedModal
      opened={modalOpened}
      onClose={
        accepted
          ? () => onClose?.()
          : () => {
              void signOut('/auth/login?errorCode=EulaTermsNotAccepted');
            }
      }
      onConfirm={() =>
        acceptEula({})
          .then(() => setAccepted(true))
          .then(refreshUser)
          .then(onEulaAccepted)
      }
      withCloseButton={false}
      closeOnConfirm={false}
      disabled={loading || !eulaEndReached}
      loading={saving || loadingUser}
      confirmationText="Accept"
      cancellationText="Decline"
      title="End-User License Agreement"
      size="xl"
      leftComponent={<Text c="red">Please accept the terms to continue.</Text>}
      zIndex={EULA_MODAL_Z_INDEX}
    >
      {isNotNil(data) ? (
        <>
          {parse(data.content)}
          <TriggerOnVisible onVisible={() => setEulaEndReached(true)} />
        </>
      ) : (
        <Flex flex="1 0 0" justify="center" align="center" mih={700}>
          <Loader size="lg" />
        </Flex>
      )}
    </WrappedModal>
  );
};
